import { useState, useEffect, useRef } from "react";
import { ClockCounterClockwise, Check, Plus } from "@phosphor-icons/react";
import { API } from "./constants";
import { LogPickerSheet } from "./LogPickerSheet";
import { formatDateIT } from "@/lib/formatDate";

// Coerce a raw server value (number, null, undefined) into the string the
// picker/input cells expect. IMPORTANT: keeps the literal "0" for bodyweight
// exercises (`s.weight_kg = 0`) — a naïve `s.weight_kg || ""` would drop it.
const toCell = (v) => (v === null || v === undefined || v === "" ? "" : String(v));

const setsFromLog = (log, numSets) => {
  if (log?.sets?.length) {
    const mapped = log.sets.map((s) => ({ weight_kg: toCell(s.weight_kg), reps: toCell(s.reps) }));
    // Pad up to numSets if the log has fewer entries than the workout expects.
    while (mapped.length < numSets) mapped.push({ weight_kg: "", reps: "" });
    return mapped;
  }
  return Array.from({ length: numSets }, () => ({ weight_kg: "", reps: "" }));
};

export function ExerciseLogForm({ exercise, workoutId, token, existingLog, previousLog, onSaved }) {
  const numSets = parseInt(exercise.sets) || 3;
  const prevSets = previousLog?.sets || [];
  const [sets, setSets] = useState(() => setsFromLog(existingLog, numSets));
  const [saving, setSaving] = useState(false);
  const [justSavedAt, setJustSavedAt] = useState(0); // timestamp for transient "Salvato" feedback
  const [pickerIdx, setPickerIdx] = useState(null);

  // When `existingLog` transitions from empty → populated (parent finished
  // fetching /logs), or the log identity changes because we navigated to a
  // block whose data landed AFTER mount, hydrate the input cells so the user
  // sees the already-saved values instead of empty placeholders.
  // Guard: skip while the picker is open or a save is in-flight to avoid
  // clobbering the user's active edits.
  const lastAppliedLogIdRef = useRef(existingLog?.id ?? null);
  useEffect(() => {
    if (pickerIdx != null || saving) return;
    const nextId = existingLog?.id ?? null;
    if (nextId === lastAppliedLogIdRef.current) return;
    lastAppliedLogIdRef.current = nextId;
    setSets(setsFromLog(existingLog, numSets));
  }, [existingLog, numSets, pickerIdx, saving]);

  const getPrevSet = (i) => prevSets[i] || {};

  const openPicker = (i) => setPickerIdx(i);
  const closePicker = () => setPickerIdx(null);

  const persist = async (setsToSave) => {
    setSaving(true);
    try {
      const today = new Date().toISOString().split("T")[0];
      const payload = {
        workout_id: workoutId,
        exercise_name: exercise.name,
        date: today,
        sets: setsToSave.map((s, i) => ({
          set_number: i + 1,
          weight_kg: s.weight_kg ? parseFloat(s.weight_kg) : null,
          reps: s.reps ? parseInt(s.reps) : null,
        })),
      };
      const resp = await fetch(`${API}/shared/${token}/logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      let newlyBadges = [];
      try {
        const result = await resp.json();
        newlyBadges = result?.newly_unlocked_badges || [];
      } catch (err) {
        console.warn("Log response JSON parse failed:", err);
      }
      setJustSavedAt(Date.now());
      if (onSaved) onSaved(newlyBadges);
    } catch (err) {
      console.warn("persist log failed:", err);
    } finally {
      setSaving(false);
    }
  };

  const confirmPicker = async (kg, reps) => {
    if (pickerIdx == null) return;
    const updated = [...sets];
    updated[pickerIdx] = { weight_kg: String(kg), reps: String(reps) };
    setSets(updated);
    setPickerIdx(null);       // close picker (no auto-advance)
    await persist(updated);   // save immediately
  };

  const restorePrevious = () => {
    if (pickerIdx == null) return;
    const prev = getPrevSet(pickerIdx);
    const updated = [...sets];
    updated[pickerIdx] = {
      weight_kg: prev.weight_kg ? String(prev.weight_kg) : updated[pickerIdx].weight_kg,
      reps: prev.reps ? String(prev.reps) : updated[pickerIdx].reps,
    };
    setSets(updated);
  };

  const currentPrev = pickerIdx != null ? getPrevSet(pickerIdx) : {};
  const savedRecently = Date.now() - justSavedAt < 2200;

  return (
    <div className="mt-2 space-y-2" data-testid={`log-form-${exercise.name}`}>
      <div className="flex items-center justify-between px-1 min-h-[16px]">
        {previousLog ? (
          <p className="text-[9px] text-zinc-600 flex items-center gap-1">
            <ClockCounterClockwise size={10} /> Ultimo log: {formatDateIT(previousLog.date)}
          </p>
        ) : <span />}
        {saving && (
          <span className="flex items-center gap-1 text-[10px] text-zinc-500" data-testid={`log-saving-${exercise.name}`}>
            <span className="w-2.5 h-2.5 border-2 border-zinc-500 border-t-transparent rounded-full animate-spin" />
            Salvataggio…
          </span>
        )}
        {!saving && savedRecently && (
          <span
            className="flex items-center gap-1 text-[10px] font-semibold text-[#34C759]"
            data-testid={`log-saved-${exercise.name}`}
          >
            <Check size={11} weight="bold" /> Salvato
          </span>
        )}
      </div>
      <div className="grid grid-cols-[auto_1fr] gap-2 items-center px-1">
        <span className="text-[9px] text-zinc-600 w-6"></span>
        <span className="text-[9px] text-zinc-500 uppercase font-bold tracking-wider text-center">Reps × Kg</span>
      </div>
      {sets.map((s, i) => {
        const prev = getPrevSet(i);
        const prevWeight = prev.weight_kg ? String(prev.weight_kg) : "";
        const prevReps = prev.reps ? String(prev.reps) : "";
        const filled = s.weight_kg !== "" && s.reps !== "";
        return (
          <div key={`set-row-${i}`} className="grid grid-cols-[auto_1fr] gap-2 items-center px-1">
            <span className="text-[10px] text-zinc-600 font-bold w-6 text-center">{i + 1}</span>
            <button
              type="button"
              onClick={() => openPicker(i)}
              className={`w-full bg-[#1A1A1A] border rounded-lg px-3 py-2.5 text-base outline-none transition-colors font-mono tabular-nums ${
                filled
                  ? "border-[#007AFF]/40 text-white"
                  : "border-[#262626] text-zinc-500 hover:border-[#007AFF]/30"
              }`}
              data-testid={`log-cell-${i}`}
              aria-label={`Serie ${i + 1}: ${filled ? `${s.reps} per ${s.weight_kg} kg` : "da inserire"}`}
            >
              {filled ? (
                <span className="grid grid-cols-[1fr_auto_1fr] items-baseline w-full">
                  <span className="text-right pr-2 text-white font-semibold">{s.reps}</span>
                  <span className="text-zinc-500 px-1">×</span>
                  <span className="text-left pl-2 text-white font-semibold">
                    {s.weight_kg}
                    <span className="text-zinc-500 text-[11px] ml-1">kg</span>
                  </span>
                </span>
              ) : prevReps || prevWeight ? (
                <span className="grid grid-cols-[1fr_auto_1fr] items-baseline w-full opacity-60">
                  <span className="text-right pr-2">{prevReps || "—"}</span>
                  <span className="px-1">×</span>
                  <span className="text-left pl-2">
                    {prevWeight || "—"}
                    <span className="text-[11px] ml-1">kg</span>
                    <Plus size={11} className="inline ml-2 mb-0.5 opacity-80" weight="bold" />
                  </span>
                </span>
              ) : (
                <span className="flex items-center justify-center gap-1.5">
                  <Plus size={12} weight="bold" /> Aggiungi
                </span>
              )}
            </button>
          </div>
        );
      })}

      <LogPickerSheet
        open={pickerIdx != null}
        setIndex={pickerIdx ?? 0}
        totalSets={sets.length}
        initialKg={pickerIdx != null ? sets[pickerIdx]?.weight_kg : ""}
        initialReps={pickerIdx != null ? sets[pickerIdx]?.reps : ""}
        previousKg={currentPrev.weight_kg || ""}
        previousReps={currentPrev.reps || ""}
        onConfirm={confirmPicker}
        onClose={closePicker}
        onRestore={restorePrevious}
      />
    </div>
  );
}
